import React, { useMemo } from "react";
import { useFormikContext } from "formik";
import MultiSelect from "components/Form/fields/MultiSelect";
import get from "lodash/get";
import { ensureAppCheckout } from "utils/hooks";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { fetchAppCheckout } from "actions/app";
import classnames from "classnames";
import isEmpty from "lodash/isEmpty";

const OptionSetOption = ({
  innerProps,
  isDisabled,
  data,
  getStyles,
  ...props
}) => {
  const optionStyles = getStyles("option", props);
  const SourceIcon = null;

  const description = data.description;

  return (
    <div
      {...innerProps}
      style={{
        ...innerProps.style,
        backgroundColor: optionStyles.backgroundColor
      }}
      className={classnames(
        "flex flex-column items-center-ns pv2 bg-white--hover bb hairline-1",
        {
          "cursor-not-allowed": isDisabled
        }
      )}
    >
      <div className="flex flex-row w-100">
        <div className="flex flex-row items-center justify-between w-100">
          {SourceIcon && (
            <div
              className="flex items-center justify-center"
              style={{ width: 32, height: 32 }}
            >
              <SourceIcon />
            </div>
          )}
          <div
            className={classnames("f6 fw6 w-100 pr1 lh-title", {
              "color-text--secondary": isDisabled,
              "pl2": !SourceIcon
            })}
          >
            {data.label}
          </div>
        </div>
      </div>
      <div className="flex flex-row items-center justify-between w-100 pt2 pl2">
        <div
          className={classnames("f6 lh-title w-100", {
            "color-text--secondary": isDisabled
          })}
        >
          {description}
        </div>
      </div>
    </div>
  );
};

/**
 *
 * @param {Object} props
 * @param {Object} props.name - manifest.data.content.056b51c1-3a15-46d0-9242-4be0199cfade.data.fieldGroups[0].fields[0].meta.options.sets
 *
 * @returns
 */
const OptionSetsSelect = ({ name, actions, multi = false }) => {
  const { loading, checkout } = ensureAppCheckout({
    fetchAppCheckout: actions.fetchAppCheckout
  });
  const optionSets = checkout && checkout.optionSets;
  const options = useMemo(
    () =>
      checkout && checkout.optionSets
        ? [
            {
              id: null,
              name: "None"
            },
            ...checkout.optionSets.map((option) => ({
              label: option.title,
              value: option.uuid,
              uuid: option.uuid,
              description: option.description
            }))
          ]
        : [],
    [optionSets]
  );

  const {
    values,
    touched,
    setFieldValue,
    setFieldTouched
  } = useFormikContext();

  const setsValue = get(values, name);
  const isTouched = get(touched, name);

  let selections = [];
  if (setsValue) {
    if (multi) {
      selections = options
        .filter((option) => setsValue[option.uuid])
        .map((selected) => ({
          id: selected.uuid,
          name: selected.label
        }));
    } else {
      const activeValue = Object.keys(setsValue)[0];
      if (activeValue) {
        const selected = options.find((option) => option.uuid === activeValue);

        if (selected) {
          selections.push({
            id: selected.uuid,
            name: selected.label
          });
        }
      }
    }
  }

  const selectProps = {
    value: selections,
    placeholder: "Select option set",
    options,
    disabled: loading,
    onChange: () =>
      /**
       * Select doc
       * !multi: Object | null
       * multi: Array | null
       * @param {Object|Array|null} selection
       */
      (selection) => {
        let updatedSelections = null;
        if (Array.isArray(selection)) {
          updatedSelections = selection.reduce((memo, { value }) => {
            memo[value] = true;
            return memo;
          }, {});
        } else if (selection.value) {
          updatedSelections = {
            [selection.value]: true
          };
        }
        if (isEmpty(updatedSelections)) {
          updatedSelections = null;
        }

        setFieldValue(name, updatedSelections);

        if (!isTouched) {
          setFieldTouched(name, true);
        }
      },
    isMulti: multi,
    labelTransform: (value) => value,
    components: {
      Option: OptionSetOption
    }
  };

  return <MultiSelect {...selectProps} />;
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      fetchAppCheckout
    },
    dispatch
  )
});

export default connect(null, mapDispatchToProps)(OptionSetsSelect);
